import {
  CalendarIcon,
  ClipboardIcon,
  LogoutIcon,
  UsersIcon,
  CogIcon,
  CollectionIcon,
  DotsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  PROJECT_SEARCH_PROJECT,
  PATIENT_SEARCH_PATIENT,
  ADMIN_SEARCH_USER,
  ADMIN_SEARCH_AUDIT_LOGS,
  ADMIN_SEARCH_PROJECT,
  ADMIN_SEARCH_REPORT,
  ADMIN_SEARCH_WORKFLOW,
  ADMIN_SEARCH_GROUP,
  ADMIN_SEARCH_DOMAIN,
  ADMIN_SEARCH_ROLE,
  ADMIN_SEARCH_VARIABLE,
  ADMIN_SEARCH_NOTICE,
  ADMIN_SEARCH_MESSAGE_TEMPLATE,
  ORGANIZATION_SEARCH_QUESTIONNAIRE,
  AI_AGENT_SEARCH_REDACTION_SESSION,
} from '../../common/permissions';
import { logout } from '../../store/ddpActions';
import settings from '../../common/settings';
import Logo from '../../common/components/Logo';
import branding from '../../utils/branding';
import usePermission from '../../utils/usePermission';
import usePermissionSet, {
  ACCESS_EXTERNAL_PATIENT_SEARCH,
} from '../../utils/usePermissionSet';
import QuickAccess from '../QuickAccess';
import CurrentUser from '../../models/CurrentUser';
import NavigationItem from './NavigationItem';
import NavigationHelpdesk from './NavigationHelpdesk';
import Button from '../Button';
import { getIsNavigationOpen } from '../../store/ui/drawer/selectors';
import { setIsNavigationOpen } from '../../store/ui/drawer/actions';
import { BRANDING_TYPE__CLINICIAN } from '../../common/constants/branding';

const {
  features: { disableEpisodesOfCare, enableQCPIntegration },
} = settings.public;

const QuickAccessButton = forwardRef(({ onClick, isOpen }, ref) => {
  const { t } = useTranslation();
  return (
    <Button
      ref={ref}
      type="tertiary"
      icon={<DotsHorizontalIcon />}
      onClick={onClick}
      className={`${isOpen ? 'w-full' : ''}`}
      isCentered={false}
    >
      {isOpen && t('quickAccess')}
    </Button>
  );
});

QuickAccessButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

const Navigation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const validate = useSelector(
    CurrentUser.select.getCurrentPermissionsValidator(),
  );
  const canBrowseProjects = usePermission([PROJECT_SEARCH_PROJECT]);
  const canBrowsePatients = usePermission([PATIENT_SEARCH_PATIENT]);
  const canBrowseEpisodesOfCare = true;
  const canLookupExternalPatientDatabase = usePermissionSet(
    ACCESS_EXTERNAL_PATIENT_SEARCH,
  );
  const canBrowseQuestionnaires = usePermission([
    ORGANIZATION_SEARCH_QUESTIONNAIRE,
  ]);
  // Mirrors the gate on apiZedocGetSentimentAnalysisToken. Hook called
  // unconditionally so the flag can't change hook order.
  const hasRedactionSessionAccess = usePermission([
    AI_AGENT_SEARCH_REDACTION_SESSION,
  ]);
  const canBrowseSentimentAnalysis =
    enableQCPIntegration && hasRedactionSessionAccess;
  const canBrowseSettings =
    validate(ADMIN_SEARCH_USER) ||
    validate(ADMIN_SEARCH_AUDIT_LOGS) ||
    validate(ADMIN_SEARCH_PROJECT) ||
    validate(ADMIN_SEARCH_REPORT) ||
    validate(ADMIN_SEARCH_WORKFLOW) ||
    validate(ADMIN_SEARCH_GROUP) ||
    validate(ADMIN_SEARCH_DOMAIN) ||
    validate(ADMIN_SEARCH_ROLE) ||
    validate(ADMIN_SEARCH_VARIABLE) ||
    validate(ADMIN_SEARCH_NOTICE);
  // TECH-2730 (MR review round 8). ADMIN_SEARCH_MESSAGE_TEMPLATE is deliberately
  // NOT in that list. The Message templates entry now also requires
  // ADMIN_SEARCH_GROUP, and anyone holding that already matches the clause
  // above — so including it could only open an empty Settings section for a
  // role granted the message-template permissions on their own.
  const userId = useSelector(CurrentUser.select.userId());

  const handleOnLogout = () => {
    dispatch(logout());
  };

  const isNavigationOpen = useSelector(getIsNavigationOpen);
  const toggleSidebar = useCallback(
    () => dispatch(setIsNavigationOpen(!isNavigationOpen)),
    [dispatch, isNavigationOpen],
  );

  return (
    <aside
      className={`bg-surface ${
        isNavigationOpen ? '' : 'w-16'
      } flex flex-col border-ie border-divider`}
    >
      <div className="min-h-16 mx-2 flex justify-center items-center border-b border-divider">
        {isNavigationOpen ? (
          <Logo className="h-8" branding={BRANDING_TYPE__CLINICIAN} />
        ) : (
          <Logo className="h-8" />
        )}
      </div>
      <div
        className={`stack-2 p-2 ${isNavigationOpen ? 'overflow-y-auto' : ''}`}
      >
        <ul className="stack-2 border-b border-divider pb-2">
          {canBrowseProjects && (
            <NavigationItem
              testId="menu-item-projects"
              label={t('projects')}
              icon={<ClipboardIcon className="flex-shrink-0" />}
              items={[
                {
                  testId: 'menu-item-all-projects',
                  id: 'projects',
                  label: t('allProjects'),
                  to: '/projects',
                },
                {
                  testId: 'menu-item-pinned-projects',
                  id: 'pinned-projects',
                  label: t('pinnedProjects'),
                  to: '/pinned-projects',
                },
              ]}
              isOpen={isNavigationOpen}
            />
          )}
          {canBrowsePatients && (
            <NavigationItem
              testId="menu-item-patients"
              label={t('recipients', {
                context: branding,
              })}
              icon={<UsersIcon className="flex-shrink-0" />}
              items={[
                {
                  testId: 'menu-item-all-patients',
                  id: 'patients',
                  label: t('allRecipients', { context: branding }),
                  to: '/patients',
                },
                {
                  testId: 'menu-item-patients-in-project',
                  id: 'patients-in-project',
                  label: t('recipientsInProject', { context: branding }),
                  to: '/patients-in-project',
                },
                canLookupExternalPatientDatabase &&
                  settings.public.externalPatientSearch && {
                    testId: 'menu-item-external-patients',
                    id: 'external-patients',
                    label: 'External Patients',
                    to: '/external-patients',
                  },
              ].filter((x) => x)}
              isOpen={isNavigationOpen}
            />
          )}
          {!disableEpisodesOfCare && canBrowseEpisodesOfCare && (
            <NavigationItem
              testId="menu-item-episodes-of-care"
              label={t('screens:episodesOfCare.title')}
              icon={<CalendarIcon className="flex-shrink-0" />}
              items={[
                {
                  testId: 'menu-item-appointments',
                  id: 'appointments',
                  label: t('appointments'),
                  to: '/episodes-of-care/appointments',
                },
                // {
                //   testId: 'menu-item-biometrics',
                //   id: 'biometrics',
                //   label: 'Biometrics',
                //   to: '/episodes-of-care/biometrics',
                // },
                // {
                //   testId: 'menu-item-medications',
                //   id: 'medications',
                //   label: t('medications'),
                //   to: '/episodes-of-care/medications',
                // },
                {
                  testId: 'menu-item-procedures',
                  id: 'procedures',
                  label: t('procedures'),
                  to: '/episodes-of-care/procedures',
                },
                {
                  testId: 'menu-item-needs-review-queue',
                  id: 'needs-review-queue',
                  label: t('screens:episodesOfCare.needsReviewQueue.title'),
                  to: '/episodes-of-care/needs-review-queue',
                },
              ]}
              isOpen={isNavigationOpen}
            />
          )}
          {canBrowseSentimentAnalysis && (
            <NavigationItem
              testId="menu-item-sentiment-analysis"
              label={t('sentimentAnalysis')}
              icon={<SparklesIcon className="flex-shrink-0" />}
              to="/ai-tools/sentiment-analysis"
              isOpen={isNavigationOpen}
              isBeta
            />
          )}
          {canBrowseQuestionnaires && (
            <NavigationItem
              testId="menu-item-forms"
              label={t('allForms')}
              icon={<CollectionIcon className="flex-shrink-0" />}
              to="/forms"
              isOpen={isNavigationOpen}
            />
          )}
          {canBrowseSettings && (
            <NavigationItem
              testId="menu-item-my-settings"
              label={t('settings')}
              icon={<CogIcon className="flex-shrink-0" />}
              items={[
                validate(ADMIN_SEARCH_USER) && {
                  testId: 'menu-item-users',
                  id: 'users',
                  label: t('users'),
                  to: '/settings/users',
                },
                validate(ADMIN_SEARCH_AUDIT_LOGS) && {
                  testId: 'menu-item-logs',
                  id: 'logs',
                  label: t('logs'),
                  to: '/settings/logs',
                },
                validate(ADMIN_SEARCH_PROJECT) && {
                  testId: 'menu-item-projects',
                  id: 'projects',
                  label: t('projects'),
                  to: '/settings/projects',
                },
                validate(ADMIN_SEARCH_REPORT) && {
                  testId: 'menu-item-reports',
                  id: 'reports',
                  label: t('reports'),
                  to: '/settings/reports',
                },
                validate(ADMIN_SEARCH_WORKFLOW) && {
                  testId: 'menu-item-eapp',
                  id: 'eapp',
                  label: t('eApp'),
                  to: '/settings/eapp',
                },
                validate(ADMIN_SEARCH_DOMAIN) && {
                  testId: 'menu-item-domains',
                  id: 'domains',
                  label: t('domains'),
                  to: '/settings/domains',
                },
                validate(ADMIN_SEARCH_GROUP) && {
                  testId: 'menu-item-groups',
                  id: 'groups',
                  label: t('groups'),
                  to: '/settings/groups',
                },
                validate(ADMIN_SEARCH_ROLE) && {
                  testId: 'menu-item-roles',
                  id: 'roles',
                  label: t('roles'),
                  to: '/settings/roles',
                },
                validate(ADMIN_SEARCH_VARIABLE) && {
                  testId: 'menu-item-variables',
                  id: 'variables',
                  label: t('variables'),
                  to: '/settings/variables',
                },
                validate(ADMIN_SEARCH_NOTICE) && {
                  testId: 'menu-item-notice-board',
                  label: t('noticeBoard'),
                  to: '/settings/notice-board',
                },
                // TECH-2730 (MR review round 8). ADMIN_SEARCH_GROUP too: the
                // page's organisation selector is fed by apiAdminAllUsersGroups,
                // so without it the entry leads somewhere unusable. Matches the
                // route guard in routes/Router.js.
                validate(ADMIN_SEARCH_MESSAGE_TEMPLATE) &&
                  validate(ADMIN_SEARCH_GROUP) && {
                    testId: 'menu-item-message-templates',
                    label: t('messageTemplates'),
                    to: '/settings/message-templates',
                  },
                // review:manual: shown to everyone who can open Settings. apiZedocGetBenchmarkingToken requires AI_AGENT_SEARCH_ANSWERS_SHEET, so a user without it only sees the refusal inside the page. Gating it like sentiment analysis (hasRedactionSessionAccess above) is a UI change left for a separate decision.
                {
                  testId: 'menu-item-benchmarking',
                  label: t('benchmarking'),
                  to: '/settings/benchmarking',
                },
              ].filter((item) => item)}
              isOpen={isNavigationOpen}
            />
          )}
          {/* <NavigationItem
            testId="menu-item-ai-tools"
            label={t('aiTools')}
            icon={<SparklesIcon className="flex-shrink-0" />}
            items={[
              {
                testId: 'menu-item-knowledge-hub',
                id: 'knowledge-hub',
                label: t('knowledgeHub'),
                to: '/ai-tools/knowledge-hub',
              },
            ]}
            isOpen={isNavigationOpen}
            isBeta
          /> */}
        </ul>
        <ul className="stack-2">
          <NavigationHelpdesk isOpen={isNavigationOpen} />
          {userId && (
            <NavigationItem
              testId="menu-item-my-log-out"
              label={t('signOut')}
              icon={<LogoutIcon className="flex-shrink-0" />}
              onClick={handleOnLogout}
              isOpen={isNavigationOpen}
            />
          )}
        </ul>
      </div>
      <ul className="mt-auto stack-2 p-2 divide-y border-divider">
        {userId && (
          <li
            data-testid="menu-item-quick-access"
            className={`${isNavigationOpen ? '' : 'mx-auto'}`}
          >
            <QuickAccess
              as={QuickAccessButton}
              asProps={{
                isOpen: isNavigationOpen,
              }}
              position="right-end"
            />
          </li>
        )}
        <NavigationItem
          testId="menu-item-toggle-navigation"
          label={t('collapseNavigation')}
          className="pt-2"
          icon={
            isNavigationOpen ? (
              <ChevronLeftIcon className="flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="flex-shrink-0" />
            )
          }
          onClick={toggleSidebar}
          isOpen={isNavigationOpen}
          showTooltip={false}
        />
      </ul>
    </aside>
  );
};

export default Navigation;
